import React, { useState } from 'react';
import api from '@/lib/api';
import WebsiteReadyActions from '@/components/WebsiteReadyActions';

/**
 * Admin creates a blank EasyPeazy site, works on it, then Transfer hands
 * it to the organization. Invite is off unless they ask — Lincoln keeps the keys.
 */
export default function AdminCreateSiteCard({ onCreated }) {
  const blank = { site_name: '', label: '', owner_email: '', owner_name: '', business_description: '' };
  const [form, setForm] = useState(blank);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteNote, setInviteNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    setBusy(true);
    try {
      const r = await api.createAdminWebsite({
        site_name: form.site_name.trim(),
        label: form.label.trim(),
        owner_email: form.owner_email.trim(),
        owner_name: form.owner_name.trim(),
        business_description: form.business_description.trim(),
        // Never email anyone from Create. Send invite is its own button below.
        send_invite: false,
      });
      setForm(blank);
      setInviteEmail('');
      setInviteNote('');
      setResult(r.data);
      if (typeof onCreated === 'function') onCreated(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (err.message || 'Could not create that website.'));
    }
    setBusy(false);
  };

  const sendInvite = async () => {
    const site = result?.website;
    if (!site?.id || !inviteEmail.trim()) return;
    setInviteBusy(true);
    setInviteNote('');
    try {
      const r = await api.transferWebsiteOwner(site.id, { owner_email: inviteEmail.trim(), send_invite: true });
      setInviteNote(r.data?.owner?.invited ? `Invite sent to ${inviteEmail.trim()}.` : ((r.data?.notes || []).join(' ') || 'Invite was not sent.'));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setInviteNote(typeof detail === 'string' ? detail : 'Could not send the invite.');
    }
    setInviteBusy(false);
  };

  const site = result?.website;
  const notes = result?.notes || [];

  return (
    <div className="rounded-xl border border-indigo-500/30 bg-slate-900/40 p-5" data-testid="admin-create-site-card">
      <h3 className="text-lg font-semibold text-white mb-1" style={{ fontFamily: 'Outfit' }}>Create a website</h3>
      <p className="text-xs text-slate-400 mb-4">
        No current site to bring over. You work on it here. Transfer on this tab when the organization should own it.
      </p>
      <form onSubmit={submit} className="space-y-2">
        <input
          required
          value={form.site_name}
          onChange={(e) => setForm((f) => ({ ...f, site_name: e.target.value }))}
          placeholder="Site name"
          className="w-full bg-slate-900 border border-slate-700 rounded-lg h-9 px-3 text-sm text-white placeholder-slate-600"
          data-testid="create-site-name"
        />
        <input
          required
          value={form.label}
          onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
          placeholder="label (label.easypeazy.site)"
          className="w-full bg-slate-900 border border-slate-700 rounded-lg h-9 px-3 text-sm text-white placeholder-slate-600"
          data-testid="create-site-label"
        />
        <input
          required
          type="email"
          value={form.owner_email}
          onChange={(e) => setForm((f) => ({ ...f, owner_email: e.target.value }))}
          placeholder="Your email (or the owner, if they already should)"
          className="w-full bg-slate-900 border border-slate-700 rounded-lg h-9 px-3 text-sm text-white placeholder-slate-600"
          data-testid="create-site-owner-email"
        />
        <input
          value={form.owner_name}
          onChange={(e) => setForm((f) => ({ ...f, owner_name: e.target.value }))}
          placeholder="Owner name (optional)"
          className="w-full bg-slate-900 border border-slate-700 rounded-lg h-9 px-3 text-sm text-white placeholder-slate-600"
          data-testid="create-site-owner-name"
        />
        <textarea
          value={form.business_description}
          onChange={(e) => setForm((f) => ({ ...f, business_description: e.target.value }))}
          placeholder="What they do and where (e.g. Handyman services in Vidalia, GA)"
          rows={2}
          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600"
          data-testid="create-site-description"
        />
        <button
          type="submit"
          disabled={busy}
          className="w-full bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white h-9 rounded-lg text-sm"
          data-testid="create-site-submit"
        >
          {busy ? 'Creating…' : 'Create website'}
        </button>
        {error ? <p className="text-xs text-red-400" data-testid="create-site-error">{error}</p> : null}
        {site ? (
          <div className="text-sm space-y-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="create-site-result">
            <p className="text-emerald-400 font-medium">
              {site.status === 'active'
                ? `${site.site_name || 'The website'} is live.`
                : site.status === 'needs_setup' || site.status === 'error'
                  ? `${site.site_name || 'The website'} was saved but did not publish yet. Open it from Websites & Domains to finish.`
                  : `${site.site_name || 'The website'} is publishing. Open it in a moment.`}
            </p>
            {site.site_url ? (
              <p className="text-xs">
                <span className="text-slate-400">Live link: </span>
                <a href={site.site_url} target="_blank" rel="noopener noreferrer" className="text-indigo-300 underline break-all" data-testid="create-site-live-link">{site.site_url}</a>
              </p>
            ) : null}
            <p className="text-xs text-slate-300 font-medium">Next steps</p>
            <WebsiteReadyActions website={site} testIdPrefix="create-site" />
            <div className="space-y-2 border-t border-slate-800 pt-3" data-testid="create-site-invite">
              <p className="text-xs text-slate-400">Send invite to owner — nothing is sent until you click.</p>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Owner's email"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg h-9 px-3 text-sm text-white placeholder-slate-600"
                data-testid="create-site-invite-email"
              />
              <button
                type="button"
                disabled={inviteBusy || !inviteEmail.trim()}
                onClick={sendInvite}
                className="bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-100 px-4 py-2 rounded-lg text-sm border border-slate-700"
                data-testid="create-site-send-invite"
              >
                {inviteBusy ? 'Sending…' : 'Send invite to owner'}
              </button>
              {inviteNote ? <p className="text-xs text-slate-300" data-testid="create-site-invite-note">{inviteNote}</p> : null}
            </div>
            {notes.map((n) => (
              <p key={n} className="text-amber-300/90 text-xs">{n}</p>
            ))}
          </div>
        ) : null}
      </form>
    </div>
  );
}
